import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import axiosClient from "../../../../config/axios";
import Swal from "sweetalert2";
import "./OrderLogModal.css";

const OrderLogModal = ({ isOpen, onClose, orderId }) => {
  const [orderLogs, setOrderLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [activeTab, setActiveTab] = useState("new");
  const modalRef = useRef(null);

  useEffect(() => {
    // Cada vez que se abre el modal, hacer fetch de las bitácoras
    if (isOpen && orderId) {
      fetchOrderLogs();
    }
    // Limpiar los logs cuando se cierra el modal
    if (!isOpen) {
      setOrderLogs([]);
      setNewComment("");
      setActiveTab("new");
    }
  }, [isOpen, orderId]);

  // Efecto para actualizar la lista cuando se cambia al tab de lista
  useEffect(() => {
    if (activeTab === "list" && isOpen && orderId) {
      fetchOrderLogs();
    }
  }, [activeTab, isOpen, orderId]);

  // Efecto para cerrar el modal con la tecla Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchOrderLogs = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/order-logs/order/${orderId}`);
      setOrderLogs(response.data.data || []);
    } catch (error) {
      console.error("Error fetching order logs:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar las bitácoras",
        icon: "error",
        confirmButtonText: "Cerrar",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = async () => {
    if (!newComment.trim()) {
      Swal.fire({
        title: "Atención",
        text: "Por favor ingresa un comentario",
        icon: "warning",
        confirmButtonText: "Cerrar",
      });
      return;
    }

    if (newComment.length > 2000) {
      Swal.fire({
        title: "Atención",
        text: "El comentario no puede exceder 2000 caracteres",
        icon: "warning",
        confirmButtonText: "Cerrar",
      });
      return;
    }

    setSubmitting(true);
    try {
      await axiosClient.post("/order-logs/create", {
        order_id: orderId,
        comment: newComment.trim(),
      });

      Swal.fire({
        title: "Éxito",
        text: "Bitácora creada correctamente",
        icon: "success",
        confirmButtonText: "Cerrar",
        timer: 2000,
        showConfirmButton: false,
      });

      setNewComment("");
      await fetchOrderLogs();
    } catch (error) {
      console.error("Error creating order log:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "No se pudo crear la bitácora",
        icon: "error",
        confirmButtonText: "Cerrar",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatUserName = (user) => {
    if (!user) return "Usuario desconocido";
    const name = user.name || "";
    const firstSurname = user.first_surname || "";
    const secondSurname = user.second_surname || "";
    return (
      `${name} ${firstSurname} ${secondSurname}`.trim() ||
      user.username ||
      "Usuario"
    );
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("DD/MM/YYYY [a las] HH:mm:ss");
  };

  // Componente Skeleton para la lista de bitácoras
  const OrderLogListSkeleton = () => (
    <div className="order-log-timeline">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="order-log-item">
          <div className="order-log-item-connector"></div>
          <div className="order-log-item-avatar skeleton-shimmer">
            <div className="skeleton-avatar"></div>
          </div>
          <div className="order-log-item-content-wrapper">
            <div className="order-log-item-header">
              <div className="skeleton-text skeleton-text-title"></div>
              <div className="skeleton-text skeleton-text-date"></div>
            </div>
            <div className="order-log-item-content">
              <div className="skeleton-text skeleton-text-line"></div>
              <div className="skeleton-text skeleton-text-line skeleton-text-line-short"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
        ref={modalRef}
        onClick={(e) => {
          if (e.target === modalRef.current) {
            onClose();
          }
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "900px", width: "90%" }}
        >
          <div className="modal-content order-log-modal">
            {/* Header mejorado */}
            <div className="order-log-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="order-log-header-icon">
                  <i className="fa fa-book"></i>
                </div>
                <div>
                  <h5 className="order-log-modal-title mb-0">
                    Bitácora de Seguimiento
                  </h5>
                  <p className="order-log-modal-subtitle mb-0">
                    Registro de actividades y comentarios
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body order-log-modal-body">
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                id="order-log-tabs"
                className="order-log-tabs"
              >
                <Tab
                  eventKey="new"
                  title={
                    <span>
                      <i className="fa fa-plus-circle me-2"></i>
                      Nueva Bitácora
                    </span>
                  }
                >
                  {/* Formulario para crear nueva bitácora */}
                  <div className="order-log-create-section">
                    <div className="order-log-section-header">
                      <div className="order-log-section-icon create-icon">
                        <i className="fa fa-plus-circle"></i>
                      </div>
                      <h6 className="order-log-section-title">Nueva Entrada</h6>
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label className="order-log-form-label">
                        Comentario
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        className="order-log-textarea"
                        placeholder="Ingresa el comentario de la bitácora..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        maxLength={2000}
                        disabled={submitting}
                      />
                      <div className="order-log-char-count">
                        {newComment.length}/2000 caracteres
                      </div>
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                      <Button
                        className="order-log-save-btn"
                        onClick={handleCreateLog}
                        disabled={submitting || !newComment.trim()}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-save me-2"></i>
                            Guardar Bitácora
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Tab>

                <Tab
                  eventKey="list"
                  title={
                    <span>
                      <i className="fa fa-history me-2"></i>
                      Lista de Bitácoras
                    </span>
                  }
                >
                  {/* Lista de bitácoras */}
                  <div className="order-log-list-section">
                    <div className="order-log-section-header">
                      <div className="order-log-section-icon history-icon">
                        <i className="fa fa-history"></i>
                      </div>
                      <h6 className="order-log-section-title">
                        Historial de Bitácoras
                        {orderLogs.length > 0 && (
                          <span className="order-log-count-badge">
                            {orderLogs.length}
                          </span>
                        )}
                      </h6>
                    </div>
                    {loading ? (
                      <OrderLogListSkeleton />
                    ) : orderLogs.length === 0 ? (
                      <div className="order-log-empty-state">
                        <div className="order-log-empty-icon">
                          <i className="fa fa-inbox"></i>
                        </div>
                        <p className="order-log-empty-text">
                          No hay bitácoras registradas aún
                        </p>
                        <p className="order-log-empty-subtext">
                          Crea la primera entrada para comenzar el seguimiento
                        </p>
                      </div>
                    ) : (
                      <div className="order-log-timeline">
                        {orderLogs.map((log, index) => (
                          <div key={log.id} className="order-log-item">
                            <div className="order-log-item-connector"></div>
                            <div className="order-log-item-avatar">
                              <i className="fa fa-user"></i>
                            </div>
                            <div className="order-log-item-content-wrapper">
                              <div className="order-log-item-header">
                                <div className="order-log-item-user">
                                  {formatUserName(log.creator)}
                                </div>
                                <div className="order-log-item-date">
                                  <i className="fa fa-clock-o me-1"></i>
                                  {formatDateTime(log.createdAt)}
                                </div>
                              </div>
                              {log.comment && (
                                <div className="order-log-item-content">
                                  <p>{log.comment}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </div>
            <div className="modal-footer order-log-modal-footer">
              <Button className="order-log-close-btn" onClick={onClose}>
                <i className="fa fa-times me-2"></i>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderLogModal;
